import { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import { useIsMobile } from '../hooks/useIsMobile';
import ProductCard from '../components/ProductCard';

const H_PAD = 'clamp(60px, 12.5vw, 240px)';

const SORT_OPTIONS = [
  { value: 'default', label: 'Par défaut' },
  { value: 'price_asc', label: 'Prix croissant' },
  { value: 'price_desc', label: 'Prix décroissant' },
];

function SortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = SORT_OPTIONS.find(o => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: 'none', border: '1px solid #ddd', padding: '9px 16px',
          cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif",
          fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase',
          color: '#111', transition: 'border-color 0.2s',
          borderColor: open ? '#111' : '#ddd',
        }}
      >
        {current.label}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#111" strokeWidth="1.5"
          style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <polyline points="1,3 5,7 9,3" />
        </svg>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: '#fff', border: '1px solid #e8e8e8', minWidth: '100%', zIndex: 50, boxShadow: '0 8px 24px rgba(0,0,0,0.07)' }}>
          {SORT_OPTIONS.map(opt => (
            <button key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }} style={{
              display: 'block', width: '100%', padding: '11px 16px',
              background: opt.value === value ? '#f8f8f8' : '#fff',
              border: 'none', cursor: 'pointer', textAlign: 'left',
              fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase',
              color: opt.value === value ? '#111' : '#666',
              borderLeft: opt.value === value ? '2px solid #111' : '2px solid transparent',
              transition: 'all 0.15s',
            }}
              onMouseEnter={e => { if (opt.value !== value) e.currentTarget.style.background = '#f8f8f8'; }}
              onMouseLeave={e => { if (opt.value !== value) e.currentTarget.style.background = '#fff'; }}
            >{opt.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Catalogue() {
  const { products, loading } = useProducts();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get('cat') || 'Tous');
  const [sortBy, setSortBy] = useState('default');
  const query = searchParams.get('q') || '';

  const categories = ['Tous', ...Array.from(new Set(products.map(p => p.category)))];

  const filtered = useMemo(() => {
    let result = [...products];
    if (activeCategory !== 'Tous') result = result.filter(p => p.category === activeCategory);
    if (query) result = result.filter(p => p.name.toLowerCase().includes(query.toLowerCase()));
    if (sortBy === 'price_asc') result.sort((a, b) => a.price - b.price);
    if (sortBy === 'price_desc') result.sort((a, b) => b.price - a.price);
    return result;
  }, [products, activeCategory, sortBy, query]);

  const pad = isMobile ? '40px 20px' : `clamp(40px, 4vw, 60px) ${H_PAD}`;

  return (
    <main style={{ padding: pad, minHeight: '70vh' }}>
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? '16px' : '0', marginBottom: '40px',
      }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="1.5">
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="8" y2="18"/>
          </svg>
          <div style={{ display: 'flex', gap: isMobile ? '12px' : '16px', flexWrap: 'wrap' }}>
            {categories.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: "'Josefin Sans', sans-serif", fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase',
                color: activeCategory === cat ? '#111' : '#888',
                borderBottom: activeCategory === cat ? '1px solid #111' : '1px solid transparent',
                padding: '2px 0', transition: 'all 0.2s',
              }}>{cat}</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '10px', color: '#aaa', letterSpacing: '2px', textTransform: 'uppercase' }}>
            {filtered.length} produit{filtered.length !== 1 ? 's' : ''}
          </span>
          <SortDropdown value={sortBy} onChange={setSortBy} />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px', fontFamily: "'Josefin Sans', sans-serif", fontSize: '12px', letterSpacing: '2px', color: '#888' }}>Chargement...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px', fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: '#888' }}>Aucun produit trouvé.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? '16px 12px' : '32px 28px' }}>
          {filtered.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </main>
  );
}
